import React, { useState } from 'react';
import { Check, Loader, Clock, ArrowLeft, ShieldCheck, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PricingPage({ user, trialDaysLeft, API_URL }) {
  const navigate = useNavigate();
  const [subscribingPlan, setSubscribingPlan] = useState(null);
  const [error, setError] = useState(null);

  const handleSubscribe = async (plan) => {
    if (!user) {
        // Redirection vers login si l'utilisateur essaie de payer sans être co
        navigate('/');
        return;
    }
    
    setSubscribingPlan(plan);
    setError(null);
    
    try {
        const token = localStorage.getItem('token');
        
        // Appel Backend pour créer la session Stripe
        const response = await fetch(`${API_URL}/payments/create-subscription`, {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ plan })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            throw new Error(data.error || 'Impossible d\'initialiser le paiement.');
        }
        
        // Si l'API renvoie une URL Stripe (Checkout Session), on redirige
        if (data.url) {
             window.location.href = data.url; 
        } 
        // Si l'API renvoie un clientSecret (Stripe Elements), gestion spécifique requise
        else if (data.clientSecret) {
             // Pour l'instant, on alerte (à adapter selon votre intégration Stripe exacte)
             alert("Session de paiement créée. Redirection...");
        }

    } catch (err) {
        setError(err.message);
        setSubscribingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 font-sans text-slate-900">
       <div className="max-w-6xl mx-auto">
          
          {/* En-tête navigation */}
          <div className="flex justify-between items-center mb-12">
            <button 
                onClick={() => navigate('/dashboard')} 
                className="text-slate-500 hover:text-blue-600 flex items-center gap-2 transition-colors font-medium bg-white px-4 py-2 rounded-lg border border-slate-200 hover:border-blue-300 shadow-sm"
            >
                <ArrowLeft className="w-4 h-4" /> Retour au Dashboard
            </button>
          </div>
          
          {/* Bannière Fin d'essai */}
          {trialDaysLeft <= 0 && user && (
             <div className="bg-orange-50 border-l-4 border-orange-500 p-6 mb-12 flex items-start sm:items-center gap-4 rounded-r-lg shadow-sm animate-in slide-in-from-top-4">
                <div className="bg-orange-100 p-2 rounded-full">
                    <Clock className="text-orange-600 w-6 h-6" />
                </div>
                <div>
                   <h3 className="font-bold text-orange-900 text-lg">Oh non, votre période d'essai est terminée</h3>
                   <p className="text-orange-800 mt-1">Sélectionnez une offre ci-dessous pour débloquer immédiatement l'accès à votre veille juridique.</p>
                </div>
             </div>
          )}

          <div className="text-center max-w-3xl mx-auto mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight">Des tarifs clairs, sans engagement</h1>
            <p className="text-xl text-slate-500">Choisissez l'offre adaptée à la taille de votre structure. Changez de plan ou annulez à tout moment.</p>
          </div>
          
          {error && (
            <div className="max-w-md mx-auto mb-8 bg-red-50 text-red-700 p-4 rounded-lg text-center border border-red-100 flex items-center justify-center gap-2 font-medium">
                <AlertCircle className="w-5 h-5"/> {error}
            </div>
          )}

          {/* Grille des Prix */}
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12 items-start">
             
             {/* PLAN 1: STARTER */}
             <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 hover:shadow-md transition-shadow">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Starter</h3>
                <div className="text-slate-500 text-sm mb-6">Pour les indépendants</div>
                <div className="flex items-baseline mb-8">
                    <span className="text-4xl font-bold text-slate-900">49€</span>
                    <span className="text-slate-500 ml-2">/mois</span>
                </div>
                <button 
                    onClick={() => handleSubscribe('Starter')}
                    disabled={subscribingPlan !== null}
                    className="w-full py-3 px-4 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 transition-colors mb-8 disabled:opacity-50"
                >
                    {subscribingPlan === 'Starter' ? <Loader className="animate-spin w-5 h-5 mx-auto"/> : 'Choisir Starter'}
                </button>
                <ul className="space-y-4 text-sm text-slate-600">
                    {['3 thèmes de veille', 'Mise à jour hebdomadaire', 'Support par email', 'Export PDF standard'].map((feat, i) => (
                        <li key={i} className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-blue-600 flex-shrink-0" /> {feat}
                        </li>
                    ))}
                </ul>
             </div>

             {/* PLAN 2: PRO (Mis en avant) */}
             <div className="bg-white rounded-2xl shadow-xl border-2 border-blue-600 p-8 relative transform scale-105 z-10">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-bold uppercase tracking-wide shadow-md">
                    Le plus populaire
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Pro</h3>
                <div className="text-blue-600 text-sm mb-6 font-medium">Pour les PME et RH</div>
                <div className="flex items-baseline mb-8">
                    <span className="text-5xl font-bold text-slate-900">99€</span>
                    <span className="text-slate-500 ml-2">/mois</span>
                </div>
                <button 
                    onClick={() => handleSubscribe('Pro')}
                    disabled={subscribingPlan !== null}
                    className="w-full py-4 px-6 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 mb-8 disabled:opacity-70"
                >
                    {subscribingPlan === 'Pro' ? <Loader className="animate-spin w-5 h-5 mx-auto"/> : 'Commencer maintenant'}
                </button>
                <ul className="space-y-4 text-sm text-slate-700 font-medium">
                    {['Tous les thèmes illimités', 'Mise à jour QUOTIDIENNE', 'Support prioritaire', 'Règles personnalisées', 'Assistant IA Standard', 'Export PDF illimité'].map((feat, i) => (
                        <li key={i} className="flex items-start gap-3">
                            <div className="bg-blue-100 rounded-full p-0.5"><Check className="w-4 h-4 text-blue-600 flex-shrink-0" /></div> {feat}
                        </li>
                    ))}
                </ul>
             </div>

             {/* PLAN 3: ENTERPRISE */}
             <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 hover:shadow-md transition-shadow">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Enterprise</h3>
                <div className="text-slate-500 text-sm mb-6">Pour les grands comptes</div>
                <div className="flex items-baseline mb-8">
                    <span className="text-4xl font-bold text-slate-900">249€</span>
                    <span className="text-slate-500 ml-2">/mois</span>
                </div>
                <button 
                    onClick={() => handleSubscribe('Enterprise')}
                    disabled={subscribingPlan !== null}
                    className="w-full py-3 px-4 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 transition-colors mb-8 disabled:opacity-50"
                >
                    {subscribingPlan === 'Enterprise' ? <Loader className="animate-spin w-5 h-5 mx-auto"/> : 'Contacter les ventes'}
                </button>
                <ul className="space-y-4 text-sm text-slate-600">
                    {['Utilisateurs illimités', 'API Dédiée & SSO', 'Assistant IA Avancé (GPT-4)', 'Audit juridique annuel', 'SLA garanti 99.9%'].map((feat, i) => (
                        <li key={i} className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-blue-600 flex-shrink-0" /> {feat}
                        </li>
                    ))}
                </ul>
             </div>

          </div>
          
          <div className="mt-16 text-center flex flex-col items-center justify-center text-slate-400 text-sm gap-2">
            <ShieldCheck className="w-6 h-6 text-slate-300" />
            <p>Paiements 100% sécurisés par Stripe. Vos données bancaires ne transitent jamais par nos serveurs.</p>
          </div>
       </div>
    </div>
  );
}